import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  ShieldCheck, Plus, Edit, Trash2, Loader2, Check, Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_PERMISSIONS = [
  { key: 'view_dashboard', label: 'View Dashboard' },
  { key: 'manage_students', label: 'Manage Students' },
  { key: 'manage_courses', label: 'Manage Courses' },
  { key: 'manage_franchise', label: 'Manage Franchise' },
  { key: 'manage_finance', label: 'Manage Finance' },
  { key: 'view_reports', label: 'View Reports' },
  { key: 'manage_exams', label: 'Manage Exams' },
  { key: 'issue_certificates', label: 'Issue Certificates' },
];

const AdminRolesPage = () => {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    const mockRoles = [
      { id: 1, name: 'Operations Admin', description: 'Manages day-to-day operations', permissions: ['view_dashboard', 'manage_students', 'manage_franchise'] },
      { id: 2, name: 'Finance Admin', description: 'Handles financial operations', permissions: ['view_dashboard', 'manage_finance', 'view_reports'] },
      { id: 3, name: 'Course Admin', description: 'Manages courses and exams', permissions: ['view_dashboard', 'manage_courses', 'manage_exams', 'issue_certificates'] },
    ];
    try {
      const response = await axios.get(`${API}/admin/roles`);
      // Ensure we always get an array - handle both array and object responses
      const rolesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.roles || response.data?.data || mockRoles);
      setRoles(Array.isArray(rolesData) ? rolesData : mockRoles);
    } catch (error) {
      setRoles(mockRoles);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (selectedRole) {
        await axios.put(`${API}/admin/roles/${selectedRole.id}`, roleForm);
        toast.success('Role updated successfully');
      } else {
        await axios.post(`${API}/admin/roles`, roleForm);
        toast.success('Role created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error('Failed to save role');
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setRoleForm({ name: '', description: '', permissions: [] });
    setSelectedRole(null);
  };

  const openEditDialog = (role) => {
    setSelectedRole(role);
    setRoleForm({
      name: role.name,
      description: role.description,
      permissions: Array.isArray(role.permissions) ? role.permissions : []
    });
    setDialogOpen(true);
  };

  const togglePermission = (key) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-roles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Roles & Permissions</h1>
          <p className="text-slate-500 mt-1">Manage sub-admin roles</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-purple-600 hover:bg-purple-700 gap-2">
          <Plus className="w-4 h-4" />
          Create Role
        </Button>
      </div>

      {/* Roles Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((role) => (
          <div key={role.id} className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-purple-600" />
              </div>
              <Button variant="ghost" size="sm" onClick={() => openEditDialog(role)}>
                <Edit className="w-4 h-4" />
              </Button>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">{role.name}</h3>
            <p className="text-sm text-slate-500 mb-4">{role.description}</p>
            <div className="flex flex-wrap gap-1">
              {(Array.isArray(role.permissions) ? role.permissions : []).slice(0, 3).map((perm) => (
                <span key={perm} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                  {AVAILABLE_PERMISSIONS.find(p => p.key === perm)?.label || perm}
                </span>
              ))}
              {Array.isArray(role.permissions) && role.permissions.length > 3 && (
                <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded text-xs">
                  +{role.permissions.length - 3} more
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Role Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveRole} className="space-y-4">
            <div>
              <Label>Role Name</Label>
              <Input
                value={roleForm.name}
                onChange={(e) => setRoleForm({...roleForm, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={roleForm.description}
                onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
              />
            </div>
            <div>
              <Label className="mb-2 block">Permissions</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {AVAILABLE_PERMISSIONS.map((perm) => (
                  <label key={perm.key} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 rounded">
                    <input
                      type="checkbox"
                      checked={roleForm.permissions.includes(perm.key)}
                      onChange={() => togglePermission(perm.key)}
                      className="rounded"
                    />
                    <span className="text-sm">{perm.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving} className="bg-purple-600 hover:bg-purple-700">
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                {selectedRole ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRolesPage;
